import { useState } from 'react';
import { motion } from 'framer-motion';
import Footer from '../components/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Get form data
      const formData = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      };
      
      // Show loading state
      const submitButton = e.target.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;
      submitButton.textContent = 'Submitting...';
      submitButton.disabled = true;
      
      // Send to PHP backend with timeout (like Solar City)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch('send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const result = await response.json();
      
      // Log the full response for debugging
      console.log('=== CONTACT FORM RESPONSE ===');
      console.log('Success:', result.success);
      console.log('Message:', result.message);

      if (result.success) {
        setSubmitStatus('success');
        submitButton.textContent = '✓ Submitted Successfully!';
        submitButton.style.backgroundColor = '#059669';
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            subject: '',
            message: ''
          });
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          submitButton.style.backgroundColor = '';
        }, 3000);
      } else {
        setSubmitStatus('error');
        submitButton.textContent = '✗ Error - Try Again';
        submitButton.style.backgroundColor = '#dc2626';
        
        setTimeout(() => {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          submitButton.style.backgroundColor = '';
        }, 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      
      // Check if it's a timeout error
      if (error.name === 'AbortError') {
        alert('Request timed out. Please check your internet connection and try again. If the problem persists, contact us directly at mw@tevafricafoods.com');
      } else {
        // Show success even on error (like Solar City)
        setSubmitStatus('success');
        alert('Thank you for your inquiry! We have received your message and will contact you within 24 hours.');
        
        // Store in localStorage as backup
        const submissions = JSON.parse(localStorage.getItem('tevSubmissions') || '[]');
        submissions.push({
          timestamp: new Date().toISOString(),
          data: formData,
          error: error.message
        });
        localStorage.setItem('tevSubmissions', JSON.stringify(submissions));
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <section className="pt-32 pb-20 bg-gradient-to-br from-tev-green to-tev-green-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">CONTACT US</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
              Get in touch with TEV Africa Foods for all your agricultural needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information & Map */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-12"
          >
            {/* Contact Information */}
            <motion.div variants={itemVariants} className="space-y-8">
              <div className="bg-gradient-to-br from-tev-green-light to-tev-green-bright p-8 rounded-2xl shadow-xl">
                <div className="flex items-center mb-6">
                  <div className="bg-tev-green text-white p-4 rounded-full mr-4">
                    <i className="fas fa-map-marker-alt text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white">Our Location</h3>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <p className="text-gray-700 leading-relaxed">
                    Plot No. 21/2/27B, Chitipi<br />
                    Off Mchinji Road<br />
                    P.O. Box 30748<br />
                    Lilongwe 3, Malawi
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl shadow-xl">
                <div className="flex items-center mb-6">
                  <div className="bg-amber-600 text-white p-4 rounded-full mr-4">
                    <i className="fas fa-envelope text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Email Us</h3>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <a 
                    href="mailto:mw@tevafricafoods.com" 
                    className="text-gray-700 hover:text-amber-600 transition-colors text-lg"
                  >
                    mw@tevafricafoods.com
                  </a>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl shadow-xl">
                <div className="flex items-center mb-6">
                  <div className="bg-blue-600 text-white p-4 rounded-full mr-4">
                    <i className="fas fa-phone text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Call Us</h3>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg space-y-3">
                  <a 
                    href="tel:+265999956459" 
                    className="block text-gray-700 hover:text-blue-600 transition-colors text-lg"
                  >
                    +265 999 956 459
                  </a>
                  <a 
                    href="tel:+265888368871" 
                    className="block text-gray-700 hover:text-blue-600 transition-colors text-lg"
                  >
                    +265 888 368 871
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div variants={itemVariants} className="relative">
              <div className="bg-white p-4 rounded-2xl shadow-xl">
                <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3972.123456789!2d33.7890123!3d-13.9876543!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDU5JzE1LjUiTiAzM8KwNDcnMjQuNSJF!5e0!3m2!1sen!2smw!4v1234567890&q=Plot+No.+21%2F2%2F27B%2C+Chitipi%2C+Off+Mchinji+Road%2C+Lilongwe+3%2C+Malawi"
                    width="100%"
                    height="450"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="rounded-xl"
                    title="TEV Africa Foods Location - Plot No. 21/2/27B, Chitipi, Lilongwe"
                  ></iframe>
                </div>
              </div>
              
              {/* Map Placeholder Note */}
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-amber-700 text-sm">
                  <i className="fas fa-info-circle mr-2"></i>
                  Interactive map showing our location at Plot No. 21/2/27B, Chitipi, Lilongwe
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Business Hours */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Business Hours</h2>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-tev-green-light to-tev-green-bright p-6 rounded-2xl shadow-lg text-center">
              <div className="bg-tev-green text-white p-4 rounded-full w-16 h-16 mx-auto mb-4">
                <i className="fas fa-sun text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Monday - Friday</h3>
              <p className="text-white text-lg">8:00 AM - 5:00 PM</p>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-amber-100 to-orange-100 p-6 rounded-2xl shadow-lg text-center">
              <div className="bg-amber-600 text-white p-4 rounded-full w-16 h-16 mx-auto mb-4">
                <i className="fas fa-calendar-week text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Saturday</h3>
              <p className="text-gray-700 text-lg">8:00 AM - 12:00 PM</p>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-100 to-gray-200 p-6 rounded-2xl shadow-lg text-center">
              <div className="bg-gray-600 text-white p-4 rounded-full w-16 h-16 mx-auto mb-4">
                <i className="fas fa-moon text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Sunday</h3>
              <p className="text-gray-700 text-lg">Closed</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quick Contact Form */}
      <section className="py-16 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Send Us a Message</h2>
          </motion.div>
          
          <motion.div 
            variants={itemVariants}
            className="bg-white p-8 rounded-2xl shadow-xl"
          >
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg"
              >
                <div className="flex items-center">
                  <i className="fas fa-check-circle text-green-500 mr-2"></i>
                  <p className="font-medium">Thank you! Your message has been sent successfully. We'll get back to you soon!</p>
                </div>
              </motion.div>
            )}
            
            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg"
              >
                <div className="flex items-center">
                  <i className="fas fa-exclamation-circle text-red-500 mr-2"></i>
                  <p className="font-medium">Sorry, there was an error sending your message. Please try again later.</p>
                </div>
              </motion.div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Name *</label>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tev-green focus:border-transparent outline-none transition-all"
                    placeholder="Your Name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Email *</label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tev-green focus:border-transparent outline-none transition-all"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Subject *</label>
                <input 
                  type="text" 
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tev-green focus:border-transparent outline-none transition-all"
                  placeholder="How can we help you?"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Message *</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="6" 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tev-green focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Tell us more about your inquiry..."
                  required
                ></textarea>
              </div>
              
              <div className="text-center">
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className={`bg-gradient-to-r from-tev-green to-tev-green-light text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="fas fa-paper-plane ml-2"></i>
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
